"use client"

import Link from "next/link"
import { ArrowUpRight, ExternalLink } from "lucide-react"
import { getSectorContent, type SectorContext } from "@/lib/config/sector-content"

const DEFAULT_STATS = [
  { label: "Sales Growth Delivered", value: "£5bn+", suffix: "(GBP)" },
  { label: "Countries Worldwide", value: "60+" },
  { label: "Experts Appointed", value: "2,000+" },
  { label: "Years Experience", value: "20+" },
]

interface WhyBoardChampionsSectionProps {
  sectorSlug?: string | null
}

export default function WhyBoardChampionsSection({ sectorSlug }: WhyBoardChampionsSectionProps) {
  // Get sector-specific or global default content
  const content = getSectorContent(sectorSlug)

  // Debug logging
  console.log('[WhyBoardChampionsSection] Props:', { sectorSlug })
  console.log('[WhyBoardChampionsSection] Content loaded:', {
    id: content.id,
    title: content.hTitle,
    hasResources: content.resources.length > 0,
    hasStatsOverride: !!content.statsOverride
  })

  // Always use global stats (no sector overrides)
  const stats = DEFAULT_STATS

  // Analytics tracking for resource clicks
  const handleResourceClick = (resource: { label: string; site: string; href: string }) => {
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'sector_resource_click', {
        sector: content.id,
        site: resource.site,
        label: resource.label,
        href: resource.href
      })
    }
  }
  // Reduce bottom padding when no body content
  const hasBodyContent = content.bodyParas.length > 0 || (content.resources && content.resources.length > 0)

  return (
    <section
      id="why-board-champions"
      className={`relative pt-32 -mt-2 ${hasBodyContent ? 'pb-16' : 'pb-10'}`}
      style={{
        background: 'linear-gradient(to bottom, #DFE9F4 0%, #E3EDF7 20%, #F5F8FB 100%)',
        boxShadow: '0 -1px 6px rgba(107, 155, 210, 0.12)', /* Separation glow for optical depth */
        zIndex: 20
      }}
    >
      <div className="absolute -top-px left-0 right-0 h-px bg-neutral-200/70 opacity-0" />

      <div className="mx-auto max-w-[1280px] px-6">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] items-start">
          <div className="space-y-4">
            {/* Eyebrow - Always shows */}
            <p className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.18em] text-[#7294c7]">
              {content.hEyebrow}
              <span aria-hidden="true" className="block h-px w-8 bg-[#7294c7]/40" />
            </p>

            {/* Dynamic Title */}
            <h1 className="text-3xl md:text-[36px] font-semibold tracking-[-0.01em] leading-[1.15]" style={{ color: 'var(--body-color)' }}>
              {content.hTitle}
            </h1>

            {/* Dynamic Subtitle */}
            <p className="text-[18px] md:text-[19px] leading-[1.7] text-neutral-700" dangerouslySetInnerHTML={{ __html: content.hSubtitle }} />

            {/* CTAs - Only show on global/default view */}
            {content.primaryCTAs.length > 0 && (
              <div className="flex flex-wrap items-center gap-3 pt-2">
                {content.primaryCTAs.map((cta, index) => (
                  index === 0 ? (
                    <Link
                      key={cta.href}
                      href={cta.href}
                      className="inline-flex items-center gap-2 rounded-full bg-[#7294c7] px-6 py-3 text-sm font-semibold text-white transition-all duration-300 hover:bg-[#5d7cb8] hover:shadow-md hover:-translate-y-0.5"
                    >
                      {cta.label}
                      <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                    </Link>
                  ) : (
                    <Link
                      key={cta.href}
                      href={cta.href}
                      target={cta.href.startsWith('http') ? '_blank' : undefined}
                      rel={cta.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                      className="text-sm font-medium text-[#294474] underline-offset-4 hover:text-[#1f3558] hover:underline"
                    >
                      {cta.label}
                    </Link>
                  )
                ))}
              </div>
            )}
          </div>

          <dl className="grid grid-cols-2 gap-4 rounded-3xl border border-neutral-200 bg-white p-6 md:p-8 shadow-sm">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center flex flex-col items-center justify-center gap-2">
                <dt className="text-[11px] uppercase tracking-[0.14em] text-neutral-500 font-semibold text-center max-w-[14ch]">
                  {stat.label}
                </dt>
                <dd className="text-[48px] md:text-[52px] font-bold leading-none text-[#7294c7]">
                  {stat.value}
                </dd>
                {'suffix' in stat && stat.suffix && (
                  <dd className="text-[10px] text-neutral-400 font-medium">
                    {stat.suffix}
                  </dd>
                )}
              </div>
            ))}
          </dl>
        </div>

        {/* Dynamic Body Paragraphs - Only render if content exists */}
        {content.bodyParas.length > 0 && (
          <div className="mt-12 mx-auto max-w-[960px] space-y-5 text-[18px] md:text-[19px] leading-[1.7] text-neutral-700">
            {content.bodyParas.map((para, index) => (
              <p key={index} dangerouslySetInnerHTML={{ __html: para }} />
            ))}
          </div>
        )}

        {/* Sector Resources (if available) */}
        {content.resources && content.resources.length > 0 && (
          <div className="mt-8 mx-auto max-w-[960px]">
            <h3 className="text-sm font-semibold uppercase tracking-[0.14em] text-neutral-500 mb-4">
              Sector Resources
            </h3>
            <ul className="flex flex-wrap gap-3">
              {content.resources.map((resource, index) => (
                <li key={index}>
                  <a
                    href={resource.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => handleResourceClick(resource)}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-neutral-200 bg-white text-sm text-neutral-700 hover:border-[#7294c7] hover:text-[#7294c7] hover:bg-[#7294c7]/5 transition-all"
                  >
                    <ExternalLink className="w-4 h-4" aria-hidden="true" />
                    <span className="font-medium">{resource.label}</span>
                    <span className="text-xs text-neutral-400">({resource.site})</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  )
}

// Add wave divider after the section
export function WhyBoardChampionsSectionWithWave({ sectorSlug }: { sectorSlug?: string | null }) {
  return (
    <>
      <WhyBoardChampionsSection sectorSlug={sectorSlug} />
      {/* Standardized wave transition - Why Board Champions to main content */}
      <div className="relative w-full overflow-hidden" style={{ lineHeight: 0 }} aria-hidden="true">
        <svg
          viewBox="0 0 1440 140"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
          className="absolute w-full scale-y-[-1]"
          style={{ height: '140px' }}
        >
          <path
            d="M0,70 C360,114 480,26 720,70 C960,114 1080,26 1440,70 L1440,140 L0,140 Z"
            fill="#f8fafc"
          />
        </svg>
        <div className="block md:hidden" style={{ height: '90px' }} />
        <div className="hidden md:block" style={{ height: '140px' }} />
      </div>
    </>
  )
}
